<template>
    <div ref="trizPageRef" class="flex flex-col h-full w-full">
        <!-- 当正在创建新任务时显示加载状态 -->
        <div v-if="isCreatingJob" class="flex items-center justify-center h-full">
            <div class="text-center">
                <div class="th-loading th-loading-spinner th-loading-lg"></div>
                <p class="mt-4 text-gray-600">{{ t('page.creatingTask') }}</p>
            </div>
        </div>

        <!-- 正常的TRIZ界面 -->
        <template v-else>
            <!-- Header 根据 openDetail 状态显示在不同位置 -->
            <TrizHeader v-if="!openDetail" />
            <div class="flex w-full grow overflow-hidden">
                <ChatPanel class="shrink-0">
                    <template #header>
                        <TrizHeader v-if="openDetail" />
                    </template>
                </ChatPanel>
                <div id="triz-right-panel" v-if="openDetail" class="flex-1 overflow-hidden relative">
                    <TrizPanel />
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import TrizHeader from '@/components/TrizHeader.vue';
import ChatPanel from '@/components/chat/ChatPanel.vue';
import { useMessages } from '@/composables/chat/use-messages';
import { useAiAgentFlow } from '@/composables/chat/use-ai-agent-flow';
import { useTriz } from '@/composables/use-triz';
import { useRoute, useRouter } from 'vue-router';
import { onUnmounted, watch, ref, onMounted, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { ChatApi } from '@/domains/chat/apis';
import { AuthService } from '@eureka/shared/share-tools/src/domain/services/auth-service';
import { E_ShareType } from '@eureka/services/src/types/share';
import { BiTrackService } from '@eureka/domains/src/bi-track/services/bi-track-service';
import { EurekaTrackEventType } from '@eureka/domains/src/bi-track/types';
import { addTrialDeferred } from '@eureka/utils/account'

// 🚀 性能优化：懒加载 TrizPanel，减少首屏 bundle 大小
const TrizPanel = defineAsyncComponent(() => import('@/components/triz/TrizPanel.vue'));

const { init, newQuestion } = useAiAgentFlow()
const { resetTrizData } = useTriz()
const { openDetail, initReplyMode } = useMessages();
const chatApi = injectService<ChatApi>(ChatApi);
const authService = injectService<AuthService>(AuthService);
const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const isCreatingJob = ref(false);
const trizPageRef = ref<HTMLElement | null>(null);

const biTrackService = injectService<BiTrackService>(BiTrackService);

// 创建新的TRIZ任务
const createNewTrizJob = async() => {
    try {
        isCreatingJob.value = true;
        const response = await chatApi.createTrizJob();
        router.replace(`/result/${response.triz_mind_id}`);
        biTrackService.biTrack(EurekaTrackEventType.TRIZ, {
            sourceType: 'create_task',
            eventDetailStr: {
                triz_mind_id: response.triz_mind_id,
            },
        });
    } finally {
        isCreatingJob.value = false;
    }
};

watch(() => route.params.triz_mind_id, async(trizMindId) => {
    if (!trizMindId) {
        createNewTrizJob();
        return;
    }

    newQuestion();
    resetTrizData();

    await addTrialDeferred.promise;
    await authService.shareAccessCheck({
        data_id: trizMindId as string,
        data_type: E_ShareType.TRIZ_PRO,
    });

    init(trizMindId as string);
    initReplyMode();
    biTrackService.biTrack(EurekaTrackEventType.TRIZ, {
        sourceType: 'load_task',
        eventDetailStr: {
            triz_mind_id: trizMindId,
        },
    });
}, {
    immediate: true,
});

onMounted(async() => {
    if (trizPageRef.value) {
        const { installAuthCheck } = await import('@eureka/shared/event-listener/auth-check');
        installAuthCheck(trizPageRef.value);
    }
});

onUnmounted(() => {
    newQuestion();
});
</script>
